import { useEffect, useId, useRef } from 'react';

interface TurnstileApi {
  render(container: string | HTMLElement, options: { sitekey: string; callback: (token: string) => void; 'expired-callback': () => void; theme: 'auto' }): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | undefined;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  scriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('TURNSTILE_LOAD_FAILED'));
    document.head.append(script);
  });
  return scriptPromise;
}

export function TurnstileWidget({ onToken }: { onToken: (token?: string) => void }) {
  const rawId = useId();
  const id = `turnstile-${rawId.replaceAll(':', '')}`;
  const widgetId = useRef<string | undefined>(undefined);
  const sitekey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!sitekey) return;
    let mounted = true;
    void loadTurnstile().then(() => {
      if (mounted && window.turnstile) {
        widgetId.current = window.turnstile.render(`#${id}`, {
          sitekey,
          theme: 'auto',
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(undefined),
        });
      }
    });
    return () => {
      mounted = false;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
    };
  }, [id, onToken, sitekey]);

  if (!sitekey) return null;
  return <div id={id} className="min-h-[65px]" />;
}
